import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  BlackMenueArrow,
  WhiteMenueArrow,
} from "components/shared/icons";
import { asideLinks2 } from "constants/asideLinks2";
import { DIRECTION, translate } from "services/general";
import PickIcon from "components/shared/PickIcon";
import {
  HAS_PERMISSION,
  getAsideLinkPermissionId,
  hasAsideLinkPermission,
} from "services/user";
import PermissionDebugger from "components/shared/PermissionDebugger";
import { getAppVersion } from "utils/version";
import { useElementOverflow } from "hooks";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const splitPathAndQuery = (path = "") => {
  const [pathnameOnly, query = ""] = path.split("?");
  return { pathnameOnly, query };
};

const isPathInMenu = (menuItem, currentPathname, currentSearch) => {
  if (menuItem?.path) {
    const { pathnameOnly, query } = splitPathAndQuery(menuItem.path);
    if (pathnameOnly === currentPathname) {
      if (!query) return true;
      const currentParams = new URLSearchParams(currentSearch);
      const menuParams = new URLSearchParams(query);
      for (const [key, value] of menuParams.entries()) {
        if (currentParams.get(key) !== value) return false;
      }
      return true;
    }
  }

  if (menuItem.children) {
    for (let child of menuItem.children) {
      if (isPathInMenu(child, currentPathname, currentSearch)) return true;
      if (child.children) {
        for (let subChild of child.children) {
          if (isPathInMenu(subChild, currentPathname, currentSearch))
            return true;
        }
      }
    }
  }
  return false;
};

const Menue = ({ isDark }) => {
  const [active, setActive] = useState("");
  const [popupData, setPopupData] = useState(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState({});
  const [hoverChildIndex, setHoverChildIndex] = useState(null);
  const [, setAppVersion] = useState("1.0.1");

  const { pathname, search } = useLocation();
  const menuRef = useRef(null);
  const menuContentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isOverflowing = useElementOverflow(menuRef, menuContentRef);

  const handleSelect = (i) => setActive(i === active ? "" : i);
  const closeMobileSidebar = () => {
    setMobileSubmenuOpen({});
  };
  const toggleMobileSubmenu = (parentIndex, childIndex) => {
    const key = `${parentIndex}-${childIndex}`;
    setMobileSubmenuOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const scrollByAmount = (amount) => {
    if (menuRef.current) {
      menuRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  useEffect(() => {
    setActive("");
    setPopupData(null);
  }, [pathname]);

  // Fetch app version
  useEffect(() => {
    getAppVersion().then((version) => {
      setAppVersion(version);
    });
  }, []);

  // Outside click for desktop
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the menu and the popup
      const isOutsideMenu =
        scrollContainerRef.current &&
        !scrollContainerRef.current.contains(event.target);

      // Check if click is outside popup
      const popupElement = document.querySelector('[data-popup="true"]');
      const isOutsidePopup =
        !popupElement || !popupElement.contains(event.target);

      if (isOutsideMenu && isOutsidePopup) {
        setActive("");
        setPopupData(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showPopup = (e, m, i) => {
    const rect = e.currentTarget.getBoundingClientRect();

    let leftPosition = rect.left;

    if (DIRECTION === "rtl") {
      leftPosition = rect.right - 301;

      if (leftPosition < 0) {
        leftPosition = 0;
      }
    } else {
      const popupWidth = 301;
      if (leftPosition + popupWidth > window.innerWidth) {
        leftPosition = window.innerWidth - popupWidth;
      }
    }

    setPopupData({
      id: i,
      parentPath: m.path,
      children: m.children,
      position: { top: rect.bottom, left: leftPosition },
      isRTL: DIRECTION === "rtl",
    });
  };

  const hidePopup = () => {
    setPopupData(null);
  };

  // Handle desktop menu click
  const handleDesktopMenuClick = (e, m, i) => {
    e.preventDefault();

    // If this menu has children, toggle popup
    if (m.children?.length) {
      if (popupData && popupData.parentPath === m.path) {
        // Close popup if clicking same item
        hidePopup();
      } else {
        // Show popup for this item
        showPopup(e, m, i);
      }
    } else {
      window.location.href = m.path;
    }
  };
  const renderMenuItem = (m, i, isMobile = false) => {
    // Check if user has permission for this menu item
    const hasPermission = m.id
      ? HAS_PERMISSION(m.id)
      : m.children?.some(
          (child) =>
          hasAsideLinkPermission(child) ||
          child.children?.some((sub) => hasAsideLinkPermission(sub))
        );

    // Skip rendering if no permission
    if (!hasPermission) return null;

    const isActive = isPathInMenu(m, pathname, search);
    const baseClasses = isMobile
      ? "px-4 py-3 font-medium flex items-center gap-2 duration-200 hover:bg-brand-hover whitespace-nowrap"
      : "px-[21.34px] font-medium flex items-center h-[61px] gap-2 duration-200 hover:bg-brand-hover whitespace-nowrap";
    const activeClasses = isActive ? "bg-brand-hover" : "";
    const isExpandable = m.children?.length;

    return (
      <div key={m.path || m.text} className="relative">
        {!isMobile ? (
          // Desktop version - always use button for click handling
          m.id ? (
            <PermissionDebugger permissionId={m.id}>
              <button
                onClick={(e) => handleDesktopMenuClick(e, m, i)}
                className={`${baseClasses} ${activeClasses}`}
              >
                <span className="brightness-0 invert">{m.icon}</span>
                {translate(m.text, m.en_text)}
                {isExpandable && (
                  <WhiteMenueArrow isOpen={popupData?.id === i} />
                )}
              </button>
            </PermissionDebugger>
          ) : (
            <button
              onClick={(e) => handleDesktopMenuClick(e, m, i)}
              className={`${baseClasses} ${activeClasses}`}
            >
              <span className="brightness-0 invert">{m.icon}</span>
              {translate(m.text, m.en_text)}
              {isExpandable && <WhiteMenueArrow isOpen={popupData?.id === i} />}
            </button>
          )
        ) : (
          // Mobile version - keep existing logic
          <>
            {m.path &&
            !isPathInMenu({ path: m.path }, pathname, search) &&
            !isExpandable ? (
              m.id ? (
                <PermissionDebugger permissionId={m.id}>
                  <Link
                    to={m.path}
                    onClick={closeMobileSidebar}
                    className={`${baseClasses} ${activeClasses}`}
                  >
                    <span className="brightness-0 invert">{m.icon}</span>
                    {translate(m.text, m.en_text)}
                  </Link>
                </PermissionDebugger>
              ) : (
                <Link
                  to={m.path}
                  onClick={closeMobileSidebar}
                  className={`${baseClasses} ${activeClasses}`}
                >
                  <span className="brightness-0 invert">{m.icon}</span>
                  {translate(m.text, m.en_text)}
                </Link>
              )
            ) : m.id ? (
              <PermissionDebugger permissionId={m.id}>
                <button
                  onClick={() => handleSelect(i)}
                  className={`${baseClasses} ${activeClasses}`}
                >
                  <span className="brightness-0 invert">{m.icon}</span>
                  {translate(m.text, m.en_text)}
                  {isExpandable && <WhiteMenueArrow isOpen={i === active} />}
                </button>
              </PermissionDebugger>
            ) : (
              <button
                onClick={() => handleSelect(i)}
                className={`${baseClasses} ${activeClasses}`}
              >
                <span className="brightness-0 invert">{m.icon}</span>
                {translate(m.text, m.en_text)}
                {isExpandable && <WhiteMenueArrow isOpen={i === active} />}
              </button>
            )}
          </>
        )}

        {/* Mobile submenu */}
        {i === active && isExpandable && isMobile && (
          <ul className="bg-[#F8F8F8] text-ink font-medium text-sm custom-depth-sh z-50">
            {m.children
              .filter(
                (child) =>
                  hasAsideLinkPermission(child) ||
                  child.children?.some((sub) => hasAsideLinkPermission(sub))
              )
              .map((child, j) => {
                const childPermissionId = getAsideLinkPermissionId(child);
                const isChildExpandable = child.children?.length;
                const submenuKey = `${i}-${j}`;
                return (
                  <li key={child.path || child.text}>
                    {isChildExpandable ? (
                      childPermissionId ? (
                        <PermissionDebugger permissionId={childPermissionId}>
                          <button
                            onClick={() => toggleMobileSubmenu(i, j)}
                            className={`w-full h-[51.691px] px-6 flex items-center justify-between border-b border-[#E2E2E2] ${
                              pathname === child.path
                                ? "bg-[#EFEFEF]"
                                : "hover:bg-[#EFEFEF]"
                            }`}
                          >
                            {translate(child.text, child.en_text)}
                            <BlackMenueArrow />
                          </button>
                        </PermissionDebugger>
                      ) : (
                        <button
                          onClick={() => toggleMobileSubmenu(i, j)}
                          className={`w-full h-[51.691px] px-6 flex items-center justify-between border-b border-[#E2E2E2] ${
                            pathname === child.path
                              ? "bg-[#EFEFEF]"
                              : "hover:bg-[#EFEFEF]"
                          }`}
                        >
                          {translate(child.text, child.en_text)}
                          <BlackMenueArrow />
                        </button>
                      )
                    ) : childPermissionId ? (
                      <PermissionDebugger permissionId={childPermissionId}>
                        <Link
                          to={child.path || "#"}
                          onClick={closeMobileSidebar}
                          className={`h-[51.691px] px-6 flex items-center justify-between border-b border-[#E2E2E2] ${
                            pathname === child.path
                              ? "bg-[#EFEFEF]"
                              : "hover:bg-[#EFEFEF]"
                          }`}
                        >
                          {translate(child.text, child.en_text)}
                        </Link>
                      </PermissionDebugger>
                    ) : (
                      <Link
                        to={child.path || "#"}
                        onClick={closeMobileSidebar}
                        className={`h-[51.691px] px-6 flex items-center justify-between border-b border-[#E2E2E2] ${
                          pathname === child.path
                            ? "bg-[#EFEFEF]"
                            : "hover:bg-[#EFEFEF]"
                        }`}
                      >
                        {translate(child.text, child.en_text)}
                      </Link>
                    )}

                    {isChildExpandable &&
                      isMobile &&
                      mobileSubmenuOpen[submenuKey] && (
                        <ul className="bg-[#EFEFEF] text-ink">
                          {child.children
                          .filter((sub) => hasAsideLinkPermission(sub))
                          .map((subChild) => {
                            const subPermissionId =
                              getAsideLinkPermissionId(subChild);
                            return (
                              <li key={subChild.path || subChild.text} className="w-full">
                                  {subPermissionId ? (
                                  <PermissionDebugger
                                      permissionId={subPermissionId}
                                  >
                                    <Link
                                      to={subChild.path || "#"}
                                      onClick={closeMobileSidebar}
                                      className={`h-[51.691px] px-12 flex items-center border-b border-[#D6D6D6] w-full ${
                                        pathname === subChild.path
                                          ? "bg-gray-200/90"
                                          : "hover:bg-gray-100"
                                      }`}
                                    >
                                      {translate(
                                        subChild.text,
                                        subChild.en_text
                                      )}
                                    </Link>
                                  </PermissionDebugger>
                                ) : (
                                  <Link
                                    to={subChild.path || "#"}
                                    onClick={closeMobileSidebar}
                                    className={`h-[51.691px] px-12 flex items-center border-b border-[#D6D6D6] w-full ${
                                      pathname === subChild.path
                                        ? "bg-gray-200/90"
                                        : "hover:bg-gray-100"
                                    }`}
                                  >
                                    {translate(subChild.text, subChild.en_text)}
                                  </Link>
                                )}
                              </li>
                              );
                            })}
                        </ul>
                      )}
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    );
  };

  return (
    <>
      <section
        ref={scrollContainerRef}
        className="flex w-full h-[61px] items-center text-surface bg-brand relative bottom-px"
      >
        {isOverflowing && (
          <button
            onClick={() => scrollByAmount(DIRECTION === "rtl" ? 200 : -200)}
            className="absolute block start-0 z-50 top-0 h-full px-[13.5px] text-sm bg-chrome/70 hover:bg-chrome"
          >
            <PickIcon
              icon={DIRECTION === "rtl" ? faChevronRight : faChevronLeft}
              color="#fff"
            />
          </button>
        )}

        <div
          ref={menuRef}
          className="flex-1 overflow-x-auto scrollbar-hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <div
            ref={menuContentRef}
            className="flex items-center min-w-max px-[36px]"
          >
            {asideLinks2.map((m, i) => renderMenuItem(m, i, false))}
          </div>
        </div>

        {isOverflowing && (
          <button
            onClick={() => scrollByAmount(DIRECTION === "rtl" ? -200 : 200)}
            className="absolute block end-0 top-0 z-50 h-full px-[13.5px] text-sm bg-chrome/70 hover:bg-chrome"
          >
            <PickIcon
              icon={DIRECTION === "rtl" ? faChevronLeft : faChevronRight}
              color="#fff"
            />
          </button>
        )}
      </section>

      {/* Portal for desktop dropdown */}
      {popupData &&
        createPortal(
          <ul
            data-popup="true"
            className="absolute bg-[#F8F8F8] text-ink duration-200 font-medium text-sm custom-depth-sh z-[9999] sm:min-w-[301px] min-w-full"
            style={{
              top: popupData.position.top,
              left: popupData.position.left,
            }}
          >
            {popupData.children
              .filter(
                (child) =>
                  hasAsideLinkPermission(child) ||
                  child.children?.some((sub) => hasAsideLinkPermission(sub))
              )
              .map((child, j) => {
                const childPermissionId = getAsideLinkPermissionId(child);
                return (
                <li
                  key={child.path || child.text}
                  className="relative group w-full"
                  onMouseEnter={() => setHoverChildIndex(j)}
                  onMouseLeave={() => setHoverChildIndex(null)}
                >
                  {child.children?.length ? (
                      childPermissionId ? (
                        <PermissionDebugger permissionId={childPermissionId}>
                        <div
                          className={`relative w-full h-[51.691px] px-6 flex items-center justify-between border-b border-[#E2E2E2] cursor-pointer ${
                            pathname === child.path
                              ? "bg-[#EFEFEF]"
                              : "hover:bg-[#EFEFEF]"
                          }`}
                          dir={DIRECTION}
                        >
                          {translate(child.text, child.en_text)}
                          <BlackMenueArrow />
                        </div>
                      </PermissionDebugger>
                    ) : (
                      <div
                        className={`relative w-full h-[51.691px] px-6 flex items-center justify-between border-b border-[#E2E2E2] cursor-pointer ${
                          pathname === child.path
                            ? "bg-[#EFEFEF]"
                            : "hover:bg-[#EFEFEF]"
                        }`}
                        dir={DIRECTION}
                      >
                        {translate(child.text, child.en_text)}
                        <BlackMenueArrow />
                      </div>
                    )
                    ) : childPermissionId ? (
                      <PermissionDebugger permissionId={childPermissionId}>
                      <Link
                        to={child.path || "#"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTimeout(() => {
                            setPopupData(null);
                            setHoverChildIndex(null);
                          }, 10);
                        }}
                        className={`relative w-full h-[51.691px] px-6 flex items-center justify-between border-b border-[#E2E2E2] ${
                          pathname === child.path
                            ? "bg-[#EFEFEF]"
                            : "hover:bg-[#EFEFEF]"
                        }`}
                        dir={DIRECTION}
                      >
                        {translate(child.text, child.en_text)}
                      </Link>
                    </PermissionDebugger>
                  ) : (
                    <Link
                      to={child.path || "#"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTimeout(() => {
                          setPopupData(null);
                          setHoverChildIndex(null);
                        }, 10);
                      }}
                      className={`relative w-full h-[51.691px] px-6 flex items-center justify-between border-b border-[#E2E2E2] ${
                        pathname === child.path
                          ? "bg-[#EFEFEF]"
                          : "hover:bg-[#EFEFEF]"
                      }`}
                      dir={DIRECTION}
                    >
                      {translate(child.text, child.en_text)}
                    </Link>
                  )}

                  {child.children?.length > 0 && hoverChildIndex === j && (
                    <ul
                      className={`absolute sm:top-0 top-[51.691px] w-full sm:w-[280px] bg-[#EFEFEF] text-ink shadow-lg z-[60] ${
                        popupData?.id === 8
                          ? "sm:end-full start-0"
                          : "sm:start-full start-0"
                      }`}
                      dir={DIRECTION}
                    >
                      {child.children
                          .filter((sub) => hasAsideLinkPermission(sub))
                          .map((subChild) => {
                            const subPermissionId =
                              getAsideLinkPermissionId(subChild);
                            return (
                          <li key={subChild.path || subChild.text} className="w-full">
                              {subPermissionId ? (
                                <PermissionDebugger permissionId={subPermissionId}>
                                <Link
                                  to={subChild.path || "#"}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTimeout(() => {
                                      setPopupData(null);
                                      setHoverChildIndex(null);
                                    }, 10);
                                  }}
                                  className={`block h-[51.691px] px-6 leading-[51.691px] border-b border-[#D6D6D6] w-full ${
                                    pathname === subChild.path
                                      ? "bg-gray-200/90"
                                      : "hover:bg-gray-100"
                                  }`}
                                >
                                  {translate(subChild.text, subChild.en_text)}
                                </Link>
                              </PermissionDebugger>
                            ) : (
                              <Link
                                to={subChild.path || "#"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTimeout(() => {
                                    setPopupData(null);
                                    setHoverChildIndex(null);
                                  }, 10);
                                }}
                                className={`block h-[51.691px] px-6 leading-[51.691px] border-b border-[#D6D6D6] w-full ${
                                  pathname === subChild.path
                                    ? "bg-gray-200/90"
                                    : "hover:bg-gray-100"
                                }`}
                              >
                                {translate(subChild.text, subChild.en_text)}
                              </Link>
                            )}
                          </li>
                          );
                        })}
                    </ul>
                  )}
                </li>
                );
              })}
          </ul>,
          document.body
        )}
    </>
  );
};

export default Menue;
